/**
 * Defaults for SetupV2 Invoice step (only Source is user-required).
 * bookedWiths: 1 Manually (default), 2 Automatically
 * sendOut: false (only applies when auto-book)
 * otherRef: 1 Stripe invoice number, 2 Stripe invoice ID
 * VAT zones: 1 domestic, 2 EU, 3 abroad, 4 domestic without VAT
 * Layout / payment terms: first option from e-conomic API when empty
 */
export const INVOICE_DEFAULT_BOOKED_WITH = '1';
export const INVOICE_DEFAULT_SEND_OUT = false;
export const INVOICE_DEFAULT_OTHER_REF = '1';
export const INVOICE_DEFAULT_VAT_DOMESTIC = 1;
export const INVOICE_DEFAULT_VAT_EU = 2;
export const INVOICE_DEFAULT_VAT_ABROAD = 3;
export const INVOICE_DEFAULT_VAT_DOMESTIC_WITHOUT_VAT = 4;

export interface InvoiceDefaultOptions {
    layouts?: Array<{layoutNumber?: string | number}>;
    paymentTerms?: Array<{paymentTermsNumber?: string | number}>;
}

/** Last options loaded by Invoice step — used when persist applies defaults without options arg. */
let invoiceOptionsCache: InvoiceDefaultOptions = {};

export const setInvoiceDefaultOptionsCache = (options: InvoiceDefaultOptions): void => {
    invoiceOptionsCache = {
        layouts: options.layouts || [],
        paymentTerms: options.paymentTerms || [],
    };
};

const isEmpty = (value: unknown): boolean =>
    value === null || value === undefined || value === '';

export const applyInvoiceSetupDefaults = <T extends Record<string, any>>(
    values: T,
    options?: InvoiceDefaultOptions,
): T => {
    const opts = options || invoiceOptionsCache;
    const next: T = {...values};

    if (isEmpty(next.economic_invoice_booked_with)) {
        (next as any).economic_invoice_booked_with = INVOICE_DEFAULT_BOOKED_WITH;
    }
    if (next.economic_invoice_send_out === null || next.economic_invoice_send_out === undefined || next.economic_invoice_send_out === '') {
        (next as any).economic_invoice_send_out = INVOICE_DEFAULT_SEND_OUT;
    }
    if (isEmpty(next.economic_invoice_other_ref)) {
        (next as any).economic_invoice_other_ref = INVOICE_DEFAULT_OTHER_REF;
    }
    if (isEmpty(next.economic_invoice_vat_zone_domestic_number)) {
        (next as any).economic_invoice_vat_zone_domestic_number = INVOICE_DEFAULT_VAT_DOMESTIC;
    }
    if (isEmpty(next.economic_invoice_vat_zone_eu_number)) {
        (next as any).economic_invoice_vat_zone_eu_number = INVOICE_DEFAULT_VAT_EU;
    }
    if (isEmpty(next.economic_invoice_vat_zone_abroad_number)) {
        (next as any).economic_invoice_vat_zone_abroad_number = INVOICE_DEFAULT_VAT_ABROAD;
    }
    if (isEmpty(next.economic_invoice_vat_zone_domestic_without_vat_number)) {
        (next as any).economic_invoice_vat_zone_domestic_without_vat_number =
            INVOICE_DEFAULT_VAT_DOMESTIC_WITHOUT_VAT;
    }

    const firstLayout = opts.layouts?.[0]?.layoutNumber;
    if (isEmpty(next.economic_invoice_layout_number) && firstLayout != null && firstLayout !== '') {
        (next as any).economic_invoice_layout_number = firstLayout;
    }

    const firstPaymentTerm = opts.paymentTerms?.[0]?.paymentTermsNumber;
    if (
        isEmpty(next.economic_invoice_payment_terms_number)
        && firstPaymentTerm != null
        && firstPaymentTerm !== ''
    ) {
        (next as any).economic_invoice_payment_terms_number = firstPaymentTerm;
    }

    return next;
};

/** @deprecated use applyInvoiceSetupDefaults */
export const applyInvoiceAdvancedDefaults = applyInvoiceSetupDefaults;
